/**
 * Native React adaptation of Sergeant TAP | In Motion, published version 3.
 * Story data is unchanged. No iframe, full-window scrolling, runtime dependencies,
 * or global CSS. One self-contained, naturally sized section for a real website.
 *
 * The parent MUST pass verified existing site URLs (seekerHref, employerHref).
 * This component does not invent registration or employer destinations.
 */
import React, { useEffect, useRef, useState } from 'react';
import STORIES from './inMotionStories';
import './SergeantTapInMotion.css';

const STORY_SECONDS = 48;
const CHAPTER_SECONDS = 8;

const normalizeAudience = (value) =>
  value === 'employer' ? 'employer' : 'seeker';

export default function SergeantTapInMotion({
  initialAudience = 'seeker',
  seekerHref,
  employerHref,
  syncAudienceToUrl = false,
}) {
  const initial = normalizeAudience(initialAudience);
  const [audience, setAudience] = useState(initial);
  const [chapter, setChapter] = useState(0);
  const [playing, setPlaying] = useState(false);
  const rootRef = useRef(null);
  const scenes = STORIES[audience];
  const scene = scenes[chapter];
  const chapterCount = scenes.length;

  // Playback-state refs (avoid stale closures in observer/visibility callbacks; prevent competing
  // timers). manualPause = visitor took control (clicked a chapter / Prev / Next / Pause);
  // finished = reached the final chapter (do not loop/auto-restart); autoStarted = the first
  // meaningful-visibility start already happened; inView = section is in the viewport.
  const manualPauseRef = useRef(false);
  const finishedRef = useRef(false);
  const autoStartedRef = useRef(false);
  const inViewRef = useRef(false);
  const reducedMotionRef = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      reducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }, []);

  useEffect(() => {
    setAudience(initial);
    setChapter(0);
    setPlaying(false);
    manualPauseRef.current = false;
    finishedRef.current = false;
    autoStartedRef.current = false;
  }, [initial]);

  // Single advancement timer (reused): advance one chapter every CHAPTER_SECONDS; stop on the final
  // chapter WITHOUT looping and mark the run finished so it is not auto-resumed. 6 x 8s = 48s.
  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setTimeout(() => {
      if (chapter + 1 >= chapterCount) {
        finishedRef.current = true;
        setPlaying(false);
      } else {
        setChapter(chapter + 1);
      }
    }, CHAPTER_SECONDS * 1000);
    return () => window.clearTimeout(timer);
  }, [playing, chapter, chapterCount]);

  // Auto-start on first meaningful visibility; auto-pause when the section leaves the viewport or
  // the tab is hidden; auto-resume from the CURRENT chapter when it returns — unless the visitor
  // manually paused, the run already finished, or reduced motion is requested.
  useEffect(() => {
    const maybeResume = () => {
      if (reducedMotionRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      if (!inViewRef.current || manualPauseRef.current || finishedRef.current) return;
      if (!autoStartedRef.current) {
        autoStartedRef.current = true;
        setChapter(0); // always begin at Chapter 1 on the first automatic start
      }
      setPlaying(true);
    };

    let observer;
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        ([entry]) => {
          const visible = !!(entry && entry.isIntersecting);
          inViewRef.current = visible;
          if (visible) maybeResume();
          else setPlaying(false); // auto-pause (not a manual pause)
        },
        { threshold: 0.35 },
      );
      if (rootRef.current) observer.observe(rootRef.current);
    }

    const onVisibility = () => {
      if (document.hidden) setPlaying(false);
      else maybeResume();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (observer) observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  function chooseAudience(nextAudience) {
    const chosen = normalizeAudience(nextAudience);
    setAudience(chosen);
    setChapter(0);
    // Switching audience starts the selected story fresh at Chapter 1 (new autoplay run).
    manualPauseRef.current = false;
    finishedRef.current = false;
    autoStartedRef.current = true;
    const canAuto = !reducedMotionRef.current && inViewRef.current
      && !(typeof document !== 'undefined' && document.hidden);
    setPlaying(canAuto);
    if (syncAudienceToUrl && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (chosen === 'employer') url.searchParams.set('audience', 'employer');
      else url.searchParams.delete('audience');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }

  // Manual chapter navigation hands control to the visitor and pauses automatic advancement.
  function chooseChapter(index) {
    manualPauseRef.current = true;
    finishedRef.current = false;
    setPlaying(false);
    setChapter(Math.max(0, Math.min(chapterCount - 1, index)));
  }

  function togglePlayback() {
    if (playing) {
      manualPauseRef.current = true; // explicit Pause
      setPlaying(false);
      return;
    }
    // Play / Replay resumes automatic advancement (user-initiated; allowed even under reduced motion).
    manualPauseRef.current = false;
    finishedRef.current = false;
    autoStartedRef.current = true;
    if (chapter === chapterCount - 1) setChapter(0);
    setPlaying(true);
  }

  const targetHref = audience === 'employer' ? employerHref : seekerHref;
  const targetLabel = audience === 'employer'
    ? 'Explore employer participation'
    : 'Start my free transition';

  return (
    <section className="st-motion" ref={rootRef} aria-label="See Sergeant TAP in action">
      <div className="st-motion-inner">
        <div className="st-motion-topline">
          <div className="st-motion-introduction">
            <span className="st-motion-kicker">SEE SERGEANT TAP IN ACTION</span>
            <h2>Experience it for yourself.</h2>
            <p>Explore the journey from military experience to civilian opportunity.</p>
          </div>
          <div className="st-motion-audience" role="group" aria-label="Choose your perspective">
            <button type="button" aria-pressed={audience === 'seeker'} onClick={() => chooseAudience('seeker')}>Job Seekers</button>
            <button type="button" aria-pressed={audience === 'employer'} onClick={() => chooseAudience('employer')}>Employers</button>
          </div>
        </div>

        <div className="st-motion-stage" key={`${audience}-${chapter}`}>
          <div className="st-motion-story">
            <span className="st-motion-chapter-tag">{scene.tag}</span>
            <h3><span>{scene.title}</span><strong>{scene.accent}</strong></h3>
            <p>{scene.body}</p>
            <div className="st-motion-playrow">
              <button type="button" className="st-motion-primary" onClick={togglePlayback} aria-label={playing ? 'Pause the story' : `Play the ${STORY_SECONDS}-second story`}>
                {playing ? 'Pause story' : chapter === chapterCount - 1 ? 'Replay story' : 'Play the story'}
              </button>
              <span>{STORY_SECONDS} seconds · Six chapters · No audio required</span>
            </div>
            {targetHref ? <a className="st-motion-cta" href={targetHref}>{targetLabel} <span aria-hidden="true">→</span></a> : null}
          </div>

          <div className="st-motion-scene" aria-label="Illustrative Sergeant TAP product screen">
            <article className={`st-motion-card st-motion-kind-${scene.kind}`}>
              <div className="st-motion-card-toolbar"><span>{scene.label}</span><span className="st-motion-example-label">Illustrative example</span></div>
              <div className="st-motion-card-content">
                <span className="st-motion-card-eyebrow">{scene.eyebrow}</span>
                {scene.score ? (
                  <div className="st-motion-scoreheading">
                    <div className="st-motion-score">{scene.score}<small>MATCH</small></div>
                    <h4>{scene.cardTitle}</h4>
                  </div>
                ) : <h4>{scene.cardTitle}</h4>}
                <p className="st-motion-detail">{scene.detail}</p>
                <div className="st-motion-rows">
                  {scene.rows.map(([label, description], index) => (
                    <div className="st-motion-data-row" key={`${label}-${index}`}>
                      <b>{label}</b><span>{description}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="st-motion-card-foot">{scene.foot}</div>
            </article>
            <p className="st-motion-disclaimer">Sample profiles, examples and match scores. No live accounts accessed.</p>
          </div>
        </div>

        <div className="st-motion-bottom">
          <button type="button" className="st-motion-nav-button" disabled={chapter === 0} onClick={() => chooseChapter(chapter - 1)}>← Previous</button>
          <div className="st-motion-chapters" role="group" aria-label="Story chapters">
            {scenes.map((item, index) => (
              <button key={`${audience}-${index}`} type="button"
                className={index === chapter ? 'is-current' : ''}
                aria-label={`Chapter ${index + 1}: ${item.label}`}
                aria-current={index === chapter ? 'step' : undefined}
                onClick={() => chooseChapter(index)}>{index + 1}</button>
            ))}
          </div>
          <button type="button" className="st-motion-nav-button" disabled={chapter === chapterCount - 1} onClick={() => chooseChapter(chapter + 1)}>Next →</button>
        </div>
      </div>
    </section>
  );
}
